import { EVENTS, subscribe } from '@utilities/events';

/**
 * ProductPrice
 * Manages price display, sale/final-sale badge toggling for a product variant.
 *
 * Relies on its parent <product-overview> for product/variant state.
 * Subscribes to (via pub-sub):
 *   - product-overview:initialized
 *   - product-overview:variant-change
 *   - product-overview:no-variant
 *   - product-overview:swatch-change   (show the color's price before a variant resolves)
 */
if (!customElements.get('product-price')) {
  customElements.define('product-price', class ProductPrice extends HTMLElement {
    constructor() {
      super();

      /** @type {HTMLElement|null} */
      this.productOverview = null;

      /** Last swatch data seen (used to keep price/sale correct while no variant is resolved) */
      this.currentSwatchData = null;

      // Bound handlers for clean removal
      this.boundHandleInitialized = this.handleInitialized.bind(this);
      this.boundHandleVariantChange = this.handleVariantChange.bind(this);
      this.boundHandleNoVariant = this.handleNoVariant.bind(this);
      this.boundHandleSwatchChange = this.handleSwatchChange.bind(this);
    }

    // ─── Debug ──────────────────────────────────────────────
    allowDebug() {
      return this.productOverview?.classList.contains('debug') ?? false;
    }

    // ─── Lifecycle ──────────────────────────────────────────
    connectedCallback() {
      this.productOverview = this.closest('product-overview');
      if (!this.productOverview) {
        console.warn('ProductPrice: No <product-overview> parent found.');
        return;
      }

      this.cacheElements();

      // If the parent is already initialised, run immediately
      if (this.productOverview.classList.contains('product-overview--initialized')) {
        this.onReady(this.productOverview.product, this.productOverview.variant);
      }

      this.subscribeToEvents();
    }

    disconnectedCallback() {
      this.unsubscribeFromEvents();
      this.productOverview = null;
    }

    // ─── Element Cache ──────────────────────────────────────
    cacheElements() {
      this.priceTextElements = this.querySelectorAll('.product__price-text');
      this.saleProperties = this.querySelectorAll('.sale-property');
      this.finalSaleProperties = this.querySelectorAll('.final-sale-property');
    }

    // ─── Subscriptions ─────────────────────────────────────
    subscribeToEvents() {
      this.unsubscribers = [
        subscribe(EVENTS.PRODUCT_OVERVIEW_INITIALIZED, this.boundHandleInitialized),
        subscribe(EVENTS.PRODUCT_OVERVIEW_VARIANT_CHANGE, this.boundHandleVariantChange),
        subscribe(EVENTS.PRODUCT_OVERVIEW_NO_VARIANT, this.boundHandleNoVariant),
        subscribe(EVENTS.PRODUCT_OVERVIEW_SWATCH_CHANGE, this.boundHandleSwatchChange),
      ];
    }

    unsubscribeFromEvents() {
      if (this.unsubscribers) {
        this.unsubscribers.forEach((unsub) => unsub());
        this.unsubscribers = [];
      }
    }

    // ─── Event Handlers ─────────────────────────────────────
    handleInitialized(data) {
      if (data.productOverview !== this.productOverview) return;
      this.onReady(data.product, data.variant);
    }

    handleVariantChange(data) {
      if (data.productOverview !== this.productOverview) return;
      const { variant } = data;
      if (!variant || Object.keys(variant).length === 0) return;

      this.setPrice(variant.price_html);
      this.updateSale(this.resolveOnSale(variant));
      this.updateFinalSale(variant.is_final_sale);
    }

    /**
     * A product is "on sale" for the SALE flag if either the resolved variant is
     * discounted (compare-at > price) OR the selected color is flagged as a sale color
     * (Greyson sale_colors metafield, surfaced as swatch.on_sale). The two systems are
     * independent, so a sale color without a compare-at price still shows SALE.
     */
    resolveOnSale(variant) {
      if (variant?.on_sale === true) return true;
      return this.getActiveSwatchData()?.on_sale === true;
    }

    handleNoVariant(data) {
      if (data.productOverview !== this.productOverview) return;
      // No resolved variant: fall back to the selected color's pricing so the price
      // and SALE flag stay correct (e.g. while only the color is chosen).
      this.applySwatchPricing();
    }

    /**
     * When the color swatch changes, reflect that color's price immediately using the
     * swatch's pre-rendered price HTML — so sale colors (and any color with a different
     * price) show the correct price and SALE flag before a full variant is resolved.
     * Once a variant resolves, handleVariantChange runs setPrice again; setPrice is a
     * no-op when the HTML is identical, so there's no redundant re-render.
     */
    handleSwatchChange(data) {
      if (data.productOverview !== this.productOverview) return;
      if (!data.data) return;
      this.currentSwatchData = data.data;
      this.applySwatchPricing();
    }

    /**
     * Resolve the currently selected swatch (the one we last received via swatch-change,
     * or the initially selected swatch from the product data on first render).
     */
    getActiveSwatchData() {
      if (this.currentSwatchData && Object.keys(this.currentSwatchData).length > 0) {
        return this.currentSwatchData;
      }
      const swatches = this.productOverview?.product?.swatches;
      if (Array.isArray(swatches) && swatches.length > 0) {
        return swatches.find((swatch) => swatch.selected) || null;
      }
      return null;
    }

    /**
     * Apply price + SALE flag from the active swatch. Final sale is variant-specific and
     * unknown at the swatch level, so it's cleared until a variant resolves.
     */
    applySwatchPricing() {
      const swatchData = this.getActiveSwatchData();
      if (swatchData?.price_html) {
        this.setPrice(swatchData.price_html);
      }
      this.updateSale(swatchData?.on_sale === true);
      this.updateFinalSale(false);
    }

    // ─── Initial render when parent is ready ────────────────
    onReady(product, variant) {
      if (!variant || Object.keys(variant).length === 0) {
        this.applySwatchPricing();
        return;
      }
      this.setPrice(variant.price_html);
      this.updateSale(this.resolveOnSale(variant));
      this.updateFinalSale(variant.is_final_sale);
    }

    // ─── Price Display ──────────────────────────────────────
    setPrice(priceHtml) {
      if (!priceHtml) return;
      if (!this.priceTextElements || this.priceTextElements.length === 0) return;
      if (this.allowDebug()) {
        console.log('ProductPrice: setPrice', priceHtml);
      }
      this.priceTextElements.forEach((el) => {
        if (el.innerHTML === priceHtml) return;
        const temp = document.createElement('div');
        temp.innerHTML = priceHtml;
        el.replaceChildren(...temp.childNodes);
      });
    }

    // ─── Sale / Final-Sale Toggle ───────────────────────────
    updateSale(show) {
      if (show) {
        this.saleProperties.forEach((prop) => prop.removeAttribute('disabled'));
        this.finalSaleProperties.forEach((prop) => prop.setAttribute('disabled', true));
      } else {
        this.saleProperties.forEach((prop) => prop.setAttribute('disabled', true));
      }
    }

    updateFinalSale(show) {
      if (show) {
        this.finalSaleProperties.forEach((prop) => prop.removeAttribute('disabled'));
        this.saleProperties.forEach((prop) => prop.setAttribute('disabled', true));
      } else {
        this.finalSaleProperties.forEach((prop) => prop.setAttribute('disabled', true));
      }
    }

    // ─── Public Getters (backward-compat helpers) ───────────
    getPriceRangeHTML() {
      return this.productOverview?.product?.price_range_html ?? null;
    }
  });
}
